import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, Clock3, LifeBuoy, Sparkles } from 'lucide-react';
import {
  Eyebrow,
  HandwritingPathReveal,
  MarginNote,
  MarkerHighlight,
  RaceBib,
  Tom,
} from '@/components/foundry';
import {
  FIRST_MOVE,
  LAB_SUBSCRIPTION_CLARITY,
  PATH_PHASES,
  STUCK_STRIP,
  WELCOME_ENTITLEMENTS,
  WELCOME_GREETING,
  WELCOME_PROVENANCE,
  type PathPhase,
  type WelcomeEntitlement,
} from '../lib/welcomeFixtures';

/**
 * Welcome — first surface a Builder sees post-checkout.
 *
 * Job: close the credibility gap between the Bridge page (premium personalised
 * verdict) and the operating workspace (npm validators). Answer four questions
 * in one calm, asymmetric workbook layout:
 *
 *   1. What did I just buy?
 *   2. What do the next 30 days look like?
 *   3. What's the one thing I do first?
 *   4. When does the ~$100/mo Lab subscription start?
 *
 * Continuation of the CommandCentre workbook aesthetic — Geist + Caveat,
 * Race-bib stamps, MarkerHighlight on the subscription clarity, Tom
 * walking-on for the hero and pointing-right next to the first-move CTA.
 */
export default function Welcome() {
  const prefersReducedMotion = useReducedMotion();
  const reduced = Boolean(prefersReducedMotion);

  const handleDismiss = () => {
    try {
      localStorage.setItem('abl:welcome-seen', '1');
    } catch {
      // localStorage unavailable — the surface is still functional, the
      // Welcome route just won't auto-skip on next visit.
    }
  };

  return (
    <div className="mx-auto max-w-[1240px] px-6 pb-24 pt-10 sm:px-10 sm:pt-14">
      <Hero reducedMotion={reduced} />

      <div className="mt-14 grid grid-cols-1 gap-x-12 gap-y-14 lg:grid-cols-12">
        <main className="lg:col-span-8">
          <EntitlementsSection />
          <PathSection reducedMotion={reduced} />
          <FirstMoveSection reducedMotion={reduced} onPrimary={handleDismiss} />
        </main>

        <aside className="lg:col-span-4 lg:pl-2">
          <SubscriptionPanel />
          <StuckPanel />
          <SkipLink onSkip={handleDismiss} />
        </aside>
      </div>
    </div>
  );
}

// ── Hero ─────────────────────────────────────────────────────────────────

function Hero({ reducedMotion }: { reducedMotion: boolean }) {
  return (
    <header className="relative grid grid-cols-1 items-end gap-8 lg:grid-cols-[1fr_auto]">
      <div>
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <Eyebrow>Welcome to {WELCOME_GREETING.productName}</Eyebrow>
          <span
            className="font-caveat text-[22px] font-medium text-fd-coral"
            style={{ transform: 'rotate(-2deg)', display: 'inline-block' }}
          >
            you're in.
          </span>
        </div>

        <motion.h1
          initial={reducedMotion ? false : { opacity: 0, y: 8 }}
          animate={reducedMotion ? undefined : { opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="mt-2 [text-wrap:balance]"
        >
          <HandwritingPathReveal
            text="welcome,"
            fontSize={108}
            duration={2.0}
            delay={0.2}
            tone="ink"
            className="block"
            style={{ transform: 'rotate(-1.5deg)', transformOrigin: 'left center' }}
          />
          <span className="mt-1 block font-geist text-[28px] font-medium leading-[1.05] tracking-[-0.015em] text-fd-text-muted sm:text-[36px]">
            {WELCOME_GREETING.builderName}.
          </span>
        </motion.h1>

        <p className="mt-5 max-w-[560px] text-[15px] leading-[1.55] text-fd-text-muted">
          {WELCOME_GREETING.subhead}
        </p>
      </div>

      <div className="hidden lg:block">
        <Tom
          variant="walking-on"
          className="h-[180px] w-auto"
          style={{ transform: 'rotate(-1deg)' }}
        />
      </div>
    </header>
  );
}

// ── What you just bought ─────────────────────────────────────────────────

function EntitlementsSection() {
  return (
    <section aria-labelledby="welcome-entitlements" className="mb-14">
      <SectionHead>
        <span className="font-geist">What you</span>{' '}
        <span
          className="font-caveat text-[34px] text-fd-text"
          style={{ transform: 'rotate(-1.5deg)', display: 'inline-block' }}
        >
          just unlocked
        </span>
      </SectionHead>
      <h3 id="welcome-entitlements" className="sr-only">
        What you just unlocked
      </h3>

      <ul className="divide-y divide-fd-border border-y border-fd-border">
        {WELCOME_ENTITLEMENTS.map((item) => (
          <EntitlementRow key={item.bib} item={item} />
        ))}
      </ul>
    </section>
  );
}

function EntitlementRow({ item }: { item: WelcomeEntitlement }) {
  return (
    <li className="grid grid-cols-[auto_1fr_auto] items-start gap-x-6 gap-y-3 py-6">
      <div className="pt-0.5">
        <RaceBib n={item.bib} tone={item.stateTone} size="md" shadow="sm" />
      </div>

      <div className="min-w-0">
        <h4 className="font-geist text-[17px] font-medium leading-[1.3] tracking-[-0.01em] text-fd-text">
          {item.title}
        </h4>
        <p className="mt-1.5 max-w-[640px] text-[13.5px] leading-[1.55] text-fd-text-muted">
          {item.body}
        </p>
      </div>

      <span className="hidden shrink-0 self-start whitespace-nowrap font-geist-mono text-[10.5px] font-semibold uppercase tracking-[0.06em] text-fd-text-subtle sm:inline">
        {item.state}
      </span>
    </li>
  );
}

// ── 30-day path ──────────────────────────────────────────────────────────

function PathSection({ reducedMotion }: { reducedMotion: boolean }) {
  return (
    <section aria-labelledby="welcome-path" className="mb-14">
      <SectionHead>
        <span className="font-geist">The next</span>{' '}
        <span
          className="font-caveat text-[34px] text-fd-teal"
          style={{ transform: 'rotate(-1.5deg)', display: 'inline-block' }}
        >
          30 days
        </span>
      </SectionHead>
      <h3 id="welcome-path" className="sr-only">
        Your next 30 days
      </h3>

      <ol className="relative">
        <span
          aria-hidden
          className="absolute left-[14px] top-3 bottom-3 w-px bg-fd-border-strong sm:left-[18px]"
        />
        {PATH_PHASES.map((phase, i) => (
          <PathPhaseRow key={phase.bib} phase={phase} index={i} reducedMotion={reducedMotion} />
        ))}
      </ol>
    </section>
  );
}

function PathPhaseRow({
  phase,
  index,
  reducedMotion,
}: {
  phase: PathPhase;
  index: number;
  reducedMotion: boolean;
}) {
  return (
    <motion.li
      initial={reducedMotion ? false : { opacity: 0, y: 6 }}
      animate={reducedMotion ? undefined : { opacity: 1, y: 0 }}
      transition={{
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1],
        delay: 0.15 + index * 0.08,
      }}
      className="relative grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 pb-10 last:pb-0 sm:gap-x-8"
    >
      <div className="relative z-10 flex justify-center">
        <RaceBib n={phase.bib} tone={phase.bibTone} size="md" shadow="sm" />
      </div>

      <div className="min-w-0 pt-1">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
          <span className="font-geist-mono text-[10.5px] font-semibold uppercase tracking-[0.10em] text-fd-text-subtle">
            {phase.window}
          </span>
        </div>
        <h4 className="mt-1 font-geist text-[20px] font-medium leading-[1.2] tracking-[-0.015em] text-fd-text">
          {phase.title}
        </h4>
        <p className="mt-2 max-w-[560px] text-[13.5px] leading-[1.55] text-fd-text-muted">
          {phase.body}
        </p>

        <ul className="mt-3 space-y-1.5">
          {phase.beats.map((beat) => (
            <li
              key={beat}
              className="relative pl-5 text-[13px] leading-[1.5] text-fd-text"
            >
              <span
                aria-hidden
                className="absolute left-0 top-[0.55em] block h-1.5 w-1.5 rounded-[2px] border border-fd-text bg-fd-paper"
              />
              {beat}
            </li>
          ))}
        </ul>
      </div>
    </motion.li>
  );
}

// ── First move ───────────────────────────────────────────────────────────

function FirstMoveSection({
  reducedMotion,
  onPrimary,
}: {
  reducedMotion: boolean;
  onPrimary: () => void;
}) {
  const move = FIRST_MOVE;
  const href = `?osRoute=${move.cta.route}`;

  return (
    <motion.section
      initial={reducedMotion ? false : { opacity: 0, y: 8 }}
      animate={reducedMotion ? undefined : { opacity: 1, y: 0 }}
      transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1], delay: 0.5 }}
      aria-labelledby="welcome-first-move"
      className="relative grid grid-cols-1 gap-6 rounded-[14px] border-[1.5px] border-fd-text bg-fd-paper px-6 py-7 sm:grid-cols-[auto_1fr_auto] sm:items-center sm:px-8 sm:py-8"
      style={{ boxShadow: '4px 4px 0 0 var(--fd-text)' }}
    >
      <div className="hidden sm:block">
        <Tom variant="pointing-right" className="h-[112px] w-auto" />
      </div>

      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="inline-flex items-center gap-1.5 font-geist-mono text-[10.5px] font-semibold uppercase tracking-[0.06em] text-fd-text-subtle">
            <Sparkles className="h-3 w-3" strokeWidth={2} />
            {move.eyebrow}
          </span>
          <span className="inline-flex items-center gap-1.5 font-geist-mono text-[10.5px] font-semibold uppercase tracking-[0.06em] text-fd-text-subtle">
            <Clock3 className="h-3 w-3" strokeWidth={2} />
            {move.timebox}
          </span>
        </div>

        <h2
          id="welcome-first-move"
          className="mt-2 font-geist text-[24px] font-medium leading-[1.15] tracking-[-0.015em] text-fd-text [text-wrap:balance]"
        >
          {move.headline}
        </h2>
        <p className="mt-2 max-w-[520px] text-[13.5px] leading-[1.55] text-fd-text-muted">
          {move.body}
        </p>
      </div>

      <a
        href={href}
        onClick={onPrimary}
        className="inline-flex h-11 shrink-0 items-center justify-center gap-2 self-start rounded-[5px] border-[1.5px] border-fd-text bg-fd-accent px-5 font-geist-mono text-[12px] font-bold uppercase tracking-[0.04em] text-fd-text transition-transform hover:-translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fd-text sm:self-center"
        style={{ boxShadow: '2px 2px 0 0 var(--fd-text)' }}
      >
        {move.cta.label}
        <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.25} />
      </a>
    </motion.section>
  );
}

// ── Subscription panel ───────────────────────────────────────────────────

function SubscriptionPanel() {
  const sub = LAB_SUBSCRIPTION_CLARITY;

  return (
    <section
      aria-labelledby="welcome-subscription"
      className="rounded-[14px] border border-fd-border bg-fd-paper p-6"
    >
      <div className="flex items-start gap-3">
        <RaceBib n="L" tone="teal" size="md" shadow="sm" />
        <div className="min-w-0 flex-1">
          <h3
            id="welcome-subscription"
            className="font-geist text-[15px] font-medium text-fd-text"
          >
            The Lab subscription
          </h3>
          <p className="mt-2 text-[13.5px] leading-[1.5] text-fd-text">
            <MarkerHighlight tone="teal" duration={700} delay={500}>
              <span>{sub.highlightedLine}</span>
            </MarkerHighlight>
          </p>
          <p className="mt-2 text-[13px] leading-[1.5] text-fd-text-muted">
            {sub.body}
          </p>
        </div>
      </div>
      <p className="mt-4 border-t border-fd-border pt-3 font-geist-mono text-[10.5px] font-semibold uppercase tracking-[0.06em] text-fd-text-subtle">
        {sub.renewalNote}
      </p>
    </section>
  );
}

// ── Stuck panel ──────────────────────────────────────────────────────────

function StuckPanel() {
  const strip = STUCK_STRIP;
  const href = `?osRoute=${strip.cta.route}`;

  return (
    <section
      aria-labelledby="welcome-stuck"
      className="mt-6 rounded-[14px] border border-fd-border bg-fd-surface-1/40 p-6"
    >
      <div className="flex items-start gap-3">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-[6px] border border-fd-border bg-fd-paper text-fd-text-muted">
          <LifeBuoy className="h-4 w-4" strokeWidth={1.75} />
        </span>
        <div className="min-w-0 flex-1">
          <h3
            id="welcome-stuck"
            className="font-geist text-[14px] font-medium text-fd-text"
          >
            If you get stuck
          </h3>
          <p className="mt-1.5 text-[12.5px] leading-[1.5] text-fd-text-muted">
            {strip.body}
          </p>
          <a
            href={href}
            className="mt-3 inline-flex h-8 items-center gap-1.5 rounded-[5px] border border-fd-border bg-fd-paper px-3 font-geist-mono text-[11px] font-bold uppercase tracking-[0.04em] text-fd-text-muted transition-colors hover:border-fd-text/60 hover:text-fd-text"
          >
            {strip.cta.label}
            <ArrowRight className="h-3 w-3" strokeWidth={2} />
          </a>
        </div>
      </div>
    </section>
  );
}

// ── Skip link ────────────────────────────────────────────────────────────

function SkipLink({ onSkip }: { onSkip: () => void }) {
  return (
    <div className="mt-6 flex items-center justify-end gap-3">
      <MarginNote tilt={-2} tone="ink" size="sm" className="opacity-80">
        {WELCOME_PROVENANCE.note}
      </MarginNote>
      <a
        href="?osRoute=lab-home"
        onClick={onSkip}
        className="font-geist-mono text-[10.5px] font-semibold uppercase tracking-[0.06em] text-fd-text-subtle underline-offset-4 transition-colors hover:text-fd-text hover:underline"
      >
        Skip to your products
      </a>
    </div>
  );
}

// ── Section head ─────────────────────────────────────────────────────────

function SectionHead({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mb-5 flex items-baseline gap-2.5 font-geist text-[14px] font-semibold uppercase tracking-[0.10em] text-fd-text">
      {children}
    </h2>
  );
}
